import React from 'react';

// ─── Dashboard palette ────────────────────────────────────────────────────────
const C = {
  bg2:    '#101b2e',
  bg3:    '#141f33',
  border: '#1e2d42',
  text:   '#e8edf5',
  body:   '#b8c8dc',
  meta:   '#6a7d95',
  green:  '#00e896',
  cyan:   '#00c8ff',
  amber:  '#ffb340',
  red:    '#ff4466',
  blue:   '#4488ff',
  purple: '#9966ff',
  font:   "'DM Mono', ui-monospace, monospace",
};

// ─── Pill — transparent bg, colored text + border (readable at any darkness) ──
const Pill = ({ label, color }) => (
  <span style={{
    fontFamily:    C.font,
    fontSize:      9,
    fontWeight:    500,
    letterSpacing: '0.6px',
    color,
    background:    `${color}18`,
    border:        `1px solid ${color}50`,
    borderRadius:  4,
    padding:       '2px 7px',
    whiteSpace:    'nowrap',
  }}>
    {label}
  </span>
);

// ─── Strategy chip (option rows) ──────────────────────────────────────────────
const stratColor = strat => {
  switch (strat) {
    case 'LONG_PREMIUM': return C.blue;
    case 'DEBIT_SPREAD': return C.purple;
    default:             return C.meta;
  }
};

/**
 * PositionLedgerRow — single position row.
 * Option rows are clickable → onOptionClick(position, matchedEval).
 */
function PositionLedgerRow({ position, isOption, onOptionClick, matchedEval }) {
  const isClosed = position.status === 'closed';
  const isProfit = parseFloat(position.total_pnl_num || 0) >= 0;

  const fmtPrice = val => {
    if (val == null || val === '-') return '—';
    const n = parseFloat(val);
    return isNaN(n) ? '—' : n.toFixed(2);
  };
  const fmtSlip = val => {
    if (val == null) return '—';
    const n = parseFloat(val);
    return isNaN(n) ? '—' : n.toFixed(4);
  };

  // Inline strategy chip in Symbol cell
  const StratChip = () => {
    const er    = matchedEval?.eval_result || matchedEval || {};
    const strat = er.option_strategy;
    if (!isOption || !strat) return null;
    const col = stratColor(strat);
    return (
      <span style={{
        fontFamily:    C.font,
        fontSize:      9,
        fontWeight:    500,
        letterSpacing: '0.3px',
        display:       'inline-block',
        marginLeft:    6,
        padding:       '1px 6px',
        borderRadius:  3,
        background:    `${col}18`,
        color:         col,
        border:        `1px solid ${col}50`,
        verticalAlign: 'middle',
      }}>
        {strat.replace(/_/g, ' ')}
      </span>
    );
  };

  const rowProps = isOption && onOptionClick
    ? { onClick: () => onOptionClick(position, matchedEval), style: { cursor: 'pointer' }, title: 'Click to inspect options strategy' }
    : {};

  const td = (content, extraStyle = {}) => (
    <td style={{ fontFamily: C.font, fontSize: 11, color: C.body, padding: '7px 12px', whiteSpace: 'nowrap', ...extraStyle }}>
      {content ?? '—'}
    </td>
  );

  return (
    <tr
      className={`${isClosed ? 'closed-position' : 'active-position'}${isOption ? ' option-row' : ''}`}
      {...rowProps}
    >
      {/* Status */}
      <td style={{ padding: '7px 12px' }}>
        <Pill
          label={position.status?.toUpperCase()}
          color={isClosed ? C.meta : C.cyan}
        />
      </td>

      {/* Symbol */}
      <td style={{ fontFamily: C.font, fontSize: 11, fontWeight: 500, color: C.text, padding: '7px 12px', whiteSpace: 'nowrap' }}>
        {position.symbol}
        <StratChip />
      </td>

      {/* Option-specific: Type / Strike / Expiry */}
      {isOption && (
        <>
          <td style={{ padding: '7px 12px' }}>
            <Pill
              label={position.option_type?.toUpperCase()}
              color={position.option_type === 'call' ? C.green : C.red}
            />
          </td>
          {td(`$${position.strike}`)}
          {td(position.expiration, { fontSize: 10, color: C.meta })}
        </>
      )}

      {/* Direction */}
      <td style={{ padding: '7px 12px' }}>
        <Pill
          label={position.direction?.toUpperCase()}
          color={position.direction === 'long' ? C.green : C.red}
        />
      </td>

      {/* Quantity */}
      {td(position.quantity)}

      {/* Entry */}
      {td(`$${position.avg_entry_price}`, { color: C.text })}
      {td(position.entry_filled_at,      { fontSize: 10, color: C.meta })}
      {td(position.entry_order_type)}
      {td(fmtPrice(position.entry_limit_price))}
      <td style={{ fontFamily: C.font, fontSize: 11, padding: '7px 12px', color: parseFloat(position.entry_slippage || 0) > 0 ? C.amber : C.meta }}>
        {fmtSlip(position.entry_slippage)}
      </td>

      {/* Exit */}
      {td(isClosed ? `$${fmtPrice(position.exit_fill_price)}` : '—', { color: C.text })}
      {td(position.exit_filled_at || '—',   { fontSize: 10, color: C.meta })}
      {td(position.exit_order_type || '—')}
      {td(isClosed ? fmtPrice(position.exit_limit_price) : '—')}
      <td style={{ fontFamily: C.font, fontSize: 11, padding: '7px 12px', color: parseFloat(position.exit_slippage || 0) > 0 ? C.amber : C.meta }}>
        {isClosed ? fmtSlip(position.exit_slippage) : '—'}
      </td>

      {/* P&L */}
      <td style={{ fontFamily: C.font, fontSize: 11, fontWeight: 500, padding: '7px 12px', color: isProfit ? C.green : C.red }}>
        ${position.total_pnl}
        {!isClosed && position.unrealized_pnl_num !== 0 && (
          <span style={{ display: 'block', fontSize: 9, color: C.meta, marginTop: 1 }}>
            U: ${position.unrealized_pnl}
          </span>
        )}
      </td>

      {/* Bracket */}
      {td(fmtPrice(position.rr))}
      {td(fmtPrice(position.tp_limit))}
      {td(fmtPrice(position.sl_stop))}
      {td(fmtPrice(position.sl_limit))}

      {/* Timing */}
      {td(position.hold_time || '—',        { fontSize: 10 })}
      {td(position.entry_submitted_at,       { fontSize: 10, color: C.meta })}

      {/* Click hint */}
      {isOption && onOptionClick && (
        <td style={{ padding: '7px 8px', color: C.meta, fontSize: 10, opacity: 0.5 }}>→</td>
      )}
    </tr>
  );
}

export default PositionLedgerRow;